import React, { Component } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosService from '../../inits/axios';
import PageLoader from '../../components/Common/PageLoader';
import ReactQuill from '../../components/Common/ReactQuill';

const faqCategories = ['general', 'tech', 'account', 'privacy', 'pricing'];

// Validation schema
const validationSchema = Yup.object({
  question: Yup.string().required('Question is required'),
  category: Yup.string().required('Category is required'),
});

// Custom input component
const CustomInput = ({ field, form, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];

  return (
    <div className={props.parentClass || 'form-group'}>
      <input
        {...field}
        {...props}
        className={`${props.className || 'form-control'} ${hasError ? 'error' : ''}`}
      />
      {hasError && (
        <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
          {form.errors[field.name]}
        </div>
      )}
    </div>
  );
};

class CreateFaq extends Component {
  constructor(props) {
    super(props);
    this.state = {
      answer: '',
      answerPost: '',
      answerError: false,
      categoryError: false,
      categories: null,
      isFetching: true,
      submitError: null, // Added for error handling
      initialValues: {
        question: '',
        category: '',
      },
    };

    // Bind the ReactQuill ref
    this.quillRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.mode === 'EDIT') {
      const { question, answer, category, id } = this.props.initialValues;
      this.setState({
        answer: answer || '',
        answerPost: answer || '',
        id,
        initialValues: {
          question: question || '',
          category: category || '',
        },
      });
    }
    this.getCategories();
  }

  // Clean up on unmount to prevent memory leaks
  componentWillUnmount() {
    // Clear any pending state updates
    this.setState = () => { };
  }

  getCategories = async () => {
    try {
      const response = await axiosService.get('/categories');
      // Check if component is still mounted before updating state
      if (this.setState) {
        this.setState({
          categories: response.data || response, // Handle both response formats
          isFetching: false,
        });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (this.setState) {
        this.setState({
          isFetching: false,
          categories: [], // Set empty array on error
        });
      }
    }
  };

  // Updated onChange handler with null check
  onChange = (value) => {
    // ReactQuill can sometimes pass null
    const safeValue = value || '';
    this.setState({
      answer: safeValue,
      answerPost: safeValue,
      answerError: false
    });
  };

  // Helper function to check if content is empty
  isContentEmpty = (content) => {
    if (!content) return true;
    // Remove HTML tags and check if there's actual content
    const strippedContent = content.replace(/<[^>]*>/g, '').trim();
    return strippedContent.length === 0;
  };

  handleSubmit = async (values, { setSubmitting, setErrors }) => {
    let { answerPost, id } = this.state;

    // Better validation for empty content
    if (this.isContentEmpty(answerPost)) {
      this.setState({ answerError: true });
      setSubmitting(false);
      return;
    }

    // Clear errors
    this.setState({ answerError: false, submitError: null });

    try {
      const payload = {
        answer: answerPost,
        question: values.question,
        category: values.category
      };

      if (this.props.mode === 'EDIT') {
        payload.id = id;

        // Check if onEdit is async and await it
        if (this.props.onEdit) {
          await Promise.resolve(this.props.onEdit(payload));
        }
      } else {
        // Check if onCreate is async and await it
        if (this.props.onCreate) {
          await Promise.resolve(this.props.onCreate(payload));
        }
      }
    } catch (error) {
      console.error('Error submitting FAQ:', error);

      // Handle specific error cases
      if (error.response) {
        if (error.response.status === 401) {
          // Unauthorized - this might be causing the logout
          console.error('Authentication error - user may need to login again');
          // Don't force logout here, let the axios interceptor handle it
        } else if (error.response.status === 400) {
          // Bad request - show validation errors
          if (error.response.data && error.response.data.errors) {
            setErrors(error.response.data.errors);
          }
        } else {
          // Other server errors
          this.setState({
            submitError: error.response.data?.message || 'An error occurred while saving the FAQ'
          });
        }
      } else if (error.request) {
        // Network error
        this.setState({
          submitError: 'Network error. Please check your connection and try again.'
        });
      } else {
        // Other errors
        this.setState({
          submitError: error.message || 'An unexpected error occurred'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Quill toolbar configuration with safer options
  modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'blockquote'],
      ['clean']
    ],
    clipboard: {
      // Prevent XSS attacks
      matchVisual: false
    }
  };

  formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  render() {
    const { mode, isCreating, accessMode } = this.props;
    const { categories, isFetching, initialValues, answerError, submitError } = this.state;

    if (isFetching) {
      return <PageLoader />;
    }

    return (
      <div className="well create-piece">
        {submitError && (
          <div className="alert alert-danger" role="alert">
            {submitError}
          </div>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={this.handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <div className="row">
                <div className="col-md-3">
                  <label>Question</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="question"
                    type="text"
                    placeholder="Question"
                    component={CustomInput}
                    className={`form-control ${accessMode === 'view' ? 'disabled' : ''}`}
                    parentClass="form-group"
                    disabled={accessMode === 'view'}
                  />
                </div>

                <div className="col-md-3">
                  <label>Answer</label>
                </div>
                <div className="col-md-9">
                  <div className="form-group">
                    <ReactQuill
                      ref={this.quillRef}
                      readOnly={accessMode === 'view'}
                      value={this.state.answer}
                      onChange={this.onChange}
                      modules={this.modules}
                      formats={this.formats}
                      theme="snow"
                      placeholder="Enter your answer here..."
                      style={{ minHeight: '200px' }}
                    />
                    {answerError && (
                      <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                        Answer is required
                      </div>
                    )}
                  </div>
                </div>

                {categories && categories.length > 0 && (
                  <>
                    <div className="col-md-3">
                      <label>Category</label>
                    </div>
                    <div className="col-md-9">
                      <Field name="category">
                        {({ field, form }) => (
                          <div className="form-group">
                            <select
                              {...field}
                              className={`form-control ${accessMode === 'view' ? 'disabled' : ''}`}
                              disabled={accessMode === 'view'}
                            >
                              <option value="">Select a Category</option>
                              {categories.map((faqCategory, i) => (
                                <option key={i} value={faqCategory.name}>
                                  {faqCategory.name}
                                </option>
                              ))}
                            </select>
                            {form.touched.category && form.errors.category && (
                              <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                                {form.errors.category}
                              </div>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                  </>
                )}
              </div>

              {accessMode === 'edit' && (
                <div className="create-piece__btn-container">
                  <button
                    className="cus-btn-primary btn-block btn-lg"
                    type="submit"
                    disabled={isCreating || isSubmitting}
                  >
                    {isCreating || isSubmitting ? (
                      <span className="spinner" />
                    ) : (
                      <span>{mode === 'EDIT' ? 'Update' : 'Create'}</span>
                    )}
                  </button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default CreateFaq;
